import {
    FormControlLabel,
    Checkbox,
    Divider,
    Stack,
    TextField,
    Button,
    Typography,
    IconButton,
    List,
    ListItem,
    ListItemText,
    FormControl,
    Radio,
    RadioGroup,
    Switch,
    FormGroup
} from '@mui/material';
import { useEventContext, useGlobalContext, useQuizContext, useUserContext } from 'contexts';
import { useCallback, useEffect, useState } from 'react';
import { NavUtils, NavPages, generateRandomColor } from 'utils';
import { QuizQuestions } from 'graphql/queries/types/QuizQuestions';
import { letterByNumber } from '../grid-generator';
import { useNavigate, useParams } from 'react-router-dom';
import { BattleshipGameTypes, BattleshipQuizSetup, Orientation, ShipLocation, Team } from '../types';
import { BattleshipGenerator, ShipGenerationError } from '../battleship-generator';
import { useSnackbar } from 'components';

import ClearIcon from '@mui/icons-material/Clear';
import { cloneDeep } from 'lodash';
import { ShipManualSetup } from './ship-manual-setup';

enum SetupType {
    Live,
    SinglePlayer,
    SessionSetup
}

interface Props {
    quizQuestions: QuizQuestions;
    onQuizStart?: (setup: BattleshipQuizSetup) => void;
    onCreateQuizSession?: (setup: BattleshipQuizSetup, maxDuration?: number | null, questionTimeout?: number | null) => void;
}

/**
 * Battleship quiz setup compoment.
 * @param props
 * @returns
 */
// TODO - add buttom that gives you print sheet for teams to fill in with their desired ship locations
// TODO - add visual of ship locations in a small grid
// TODO - add option to change ship locations manually - ship by ship for a team
export const BattleshipSetup = (props: Props) => {
    const navigate = useNavigate();
    const snackbar = useSnackbar();
    const eventContext = useEventContext();
    const { quiz } = useQuizContext();
    const gContext = useGlobalContext();
    const { eventAlias } = useParams();
    const { isQuizCreatorOrEventAdmin } = useUserContext();
    const [selectedGameType, setSelectedGameType] = useState<BattleshipGameTypes>(BattleshipGameTypes.LIVE_MULTI_TEAM);
    const [manualShipSetupEnabled, setManualShipSetupEnabled] = useState(false);
    const [quizSetup, setQuizSetup] = useState<BattleshipQuizSetup>();
    const [gridColumns, setGridColumns] = useState(7);
    const [gridRows, setGridRows] = useState(6);
    const [battleshipGenerator, setBattleshipGenerator] = useState<BattleshipGenerator>(new BattleshipGenerator(gridColumns, gridRows, manualShipSetupEnabled));

    const [teams, setTeams] = useState<Team[]>([]);
    const [newTeamName, setNewTeamName] = useState<string>('');
    const [showShipLocations, setShowShipLocations] = useState(false);
    const [teamShipCount, setTeamShipCount] = useState([2, 2, 1, 1, 1]);
    const [maxGridDimention, setMaxGridDimention] = useState(7);
    // session properties
    const [cooldownSeconds, setCooldownSeconds] = useState(20);
    const [maxDurationSec, setMaxDurationSec] = useState(3600);
    const [questionTimeSec, setQuestionTimeSec] = useState(30);

    useEffect(() => {
        calculateRecommendedGrid(props.quizQuestions);
    }, [props.quizQuestions]);

    const recalculateTeamShips = (shipCount: number[], currentTeams: Team[], lBattleshipGenerator: BattleshipGenerator): void => {
        lBattleshipGenerator.clearShips();
        const newTeams = cloneDeep(currentTeams);
        newTeams.forEach((t) => {
            t.shipLocations = lBattleshipGenerator.generateRandomShipLocations(shipCount);
        });
        setTeams(newTeams);
    };

    const calculateRecommendedGrid = (data: QuizQuestions) => {
        const questionCount = data.quiz_master_quizzes_by_pk?.questions.length!;
        const sqRoot = Math.sqrt(questionCount / 2);
        const rows = Math.floor(sqRoot);
        const columns = Math.round(sqRoot);
        setGridRows(rows);
        setGridColumns(columns);
        setBattleshipGenerator(new BattleshipGenerator(columns, rows));
        setMaxGridDimention(Math.floor(questionCount / 2));
    };

    const handleGameTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedGameType(+event.target.value);
        const newQuizSetup = { ...quizSetup } as BattleshipQuizSetup;
        newQuizSetup.gameType = +event.target.value;

        if (newQuizSetup.gameType === BattleshipGameTypes.LIVE_MULTI_TEAM) {
            setTeams([]);
            battleshipGenerator.clearShips();
        } else {
            battleshipGenerator.clearShips();
            addNewTeam('The Enemy');
            changeManualShipSetup(false);
        }

        setQuizSetup(newQuizSetup);
    };

    const changeGridSize = (rows: number, columns: number) => {
        if (!props.quizQuestions) {
            return;
        }
        const questionCount = props.quizQuestions.quiz_master_quizzes_by_pk!.questions.length;
        if (questionCount < rows * columns) {
            snackbar.warning('Grid size cannot be greater than the available number of questions!');
            return;
        }
        if (rows < 2 || columns < 2) {
            snackbar.warning('Minimal grid dimention is 2!');
            return;
        }
        const newBattleshipGenerator = new BattleshipGenerator(columns, rows, manualShipSetupEnabled, battleshipGenerator.ships);

        let needToRecalculateTeams = false;
        teams
            .map((t) => t.shipLocations)
            .flat()
            .forEach((ship) => {
                if (
                    ship.startCoordinate.x >= columns ||
                    ship.startCoordinate.y >= rows ||
                    (ship.orientation === Orientation.HORIZONTAL && ship.startCoordinate.x + ship.size >= columns) ||
                    (ship.orientation === Orientation.VERTICAL && ship.startCoordinate.y + ship.size >= rows)
                ) {
                    needToRecalculateTeams = true;
                }
            });

        if (needToRecalculateTeams) {
            // recalculate ship locations for all teams only when some ship is outside of the grid or some ship count is greater than the max possible
            try {
                recalculateTeamShips(teamShipCount, teams, newBattleshipGenerator);
            } catch (error) {
                let message = error instanceof Error ? error.message : String(error);
                if (error instanceof ShipGenerationError) {
                    message =
                        'Cannot place ships in the grid during recalculation. Try increasing the grid size or reducing the number of ships.';
                    snackbar.warning(message);
                    return;
                }
                snackbar.error(message);
                return;
            }
        }

        setBattleshipGenerator(newBattleshipGenerator);

        if (columns !== gridColumns) {
            setGridColumns(columns);
        }
        if (rows !== gridRows) {
            setGridRows(rows);
        }
    };

    const onTeamShipCountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const ships = parseInt(event.target.value);
        const shipIndex = parseInt(event.target.id.substring(4));
        const newTeamShipCount = [...teamShipCount];
        newTeamShipCount[shipIndex] = ships;

        try {
            recalculateTeamShips(newTeamShipCount, teams, battleshipGenerator);
            setTeamShipCount(newTeamShipCount);
        } catch (error) {
            let message = error instanceof Error ? error.message : String(error);
            if (error instanceof ShipGenerationError) {
                message = 'Cannot place ships in the grid. Try increasing the grid size or reducing the number of ships.';
                snackbar.warning(message);
                return;
            }
            snackbar.error(message);
        }
    };

    const getShipLocationNames = (ships: ShipLocation[]): string => {
        const submarines = getShipClassLocationNames(ships.filter((s) => s.size === 1));
        const destroyers = getShipClassLocationNames(ships.filter((s) => s.size === 2));
        const cruisers = getShipClassLocationNames(ships.filter((s) => s.size === 3));
        const battleships = getShipClassLocationNames(ships.filter((s) => s.size === 4));
        const aircraftCarriers = getShipClassLocationNames(ships.filter((s) => s.size === 5));

        return `${submarines ? `Submarines: ${submarines}` : ''}
        ${destroyers ? `, Destroyers: ${destroyers}` : ''}
        ${cruisers ? `, Cruisers: ${cruisers}` : ''}
        ${battleships ? `, Battleships: ${battleships}` : ''}
        ${aircraftCarriers ? `, Aircraft Carriers: ${aircraftCarriers}` : ''}`;
    };

    const getShipClassLocationNames = (ships: ShipLocation[]): string => {
        return ships.map((d) => `[${BattleshipGenerator.getShipCoordinates(d).map((c) => `${letterByNumber(c.x)}${c.y + 1}`)}]`).join(', ');
    };

    const onNewTeamNameKeypress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            addNewTeam(newTeamName);
        }
    }

    const addNewTeamClick = () => {
        addNewTeam(newTeamName);
    };

    const addNewTeam = (teamName: string) => {
        try {
            const newTeam: Team = {
                name: teamName,
                color: generateRandomColor(),
                // add only new ships for the new team to the ships list
                shipLocations: battleshipGenerator.generateRandomShipLocations(teamShipCount)
            };
            setTeams([...teams, newTeam]);
            setNewTeamName('');
        } catch (error) {
            let message = error instanceof Error ? error.message : String(error);
            if (error instanceof ShipGenerationError) {
                message = 'Cannot place ships in the grid. Try increasing the grid size or reducing the number of ships.';
                snackbar.warning(message);
                return;
            }
            snackbar.error(message);
        }
    };

    const removeTeam = (teamName: string) => {
        battleshipGenerator.clearShips();
        const remainingTeams = teams.filter((t) => t.name !== teamName);
        setTeams(remainingTeams);
        battleshipGenerator.ships = remainingTeams.map((t) => t.shipLocations).flat();
    };

    const getGameTypeName = (gameType: BattleshipGameTypes): string => {
        switch (gameType) {
            case BattleshipGameTypes.LIVE_MULTI_TEAM:
                return 'Live Teams';
            case BattleshipGameTypes.REMOTE_COMPETITION:
                return 'Remote Competition';
            case BattleshipGameTypes.SINGLE_PLAYER:
            default:
                return 'Single Player';
        }
    };

    const onManualShipSetupChange =  (event: React.ChangeEvent<HTMLInputElement>) => {
        changeManualShipSetup(event.target.checked);
    }

    const changeManualShipSetup =  useCallback((enabled: boolean) => {
        if(enabled === manualShipSetupEnabled) {
            return;
        }

        setManualShipSetupEnabled(enabled);
        battleshipGenerator.disableGeneration = enabled;
        if(enabled) {
            battleshipGenerator.clearShips();
            setTeams(teams.map((t) => {
                return {
                    ...t,
                    shipLocations: []
                };
            }));
            return;
        }

        try {
            recalculateTeamShips(teamShipCount, teams, battleshipGenerator);
        } catch (error) {
            let message = error instanceof Error ? error.message : String(error);
            if (error instanceof ShipGenerationError) {
                message = 'Cannot place ships in the grid. Try increasing the grid size or reducing the number of ships.';
                snackbar.warning(message);
                return;
            }
            snackbar.error(message);
        }

      // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [battleshipGenerator, snackbar, teamShipCount, teams]);

    const onSetupComplete = (setupType: SetupType) => {
        const setup: BattleshipQuizSetup = {
            columns: gridColumns,
            rows: gridRows,
            cooldownSec: cooldownSeconds,
            teams,
            gameType: BattleshipGameTypes.SINGLE_PLAYER
        };

        switch (setupType) {
            case SetupType.Live:
                setup.gameType = BattleshipGameTypes.LIVE_MULTI_TEAM;
                props.onQuizStart?.(setup);
                return;

            case SetupType.SessionSetup:
                setup.gameType = BattleshipGameTypes.REMOTE_COMPETITION;
                props.onCreateQuizSession?.(
                    setup,
                    maxDurationSec > 0 ? maxDurationSec : undefined,
                    questionTimeSec > 0 ? questionTimeSec : undefined
                );
                return;

            case SetupType.SinglePlayer:
            default:
                setup.gameType = BattleshipGameTypes.SINGLE_PLAYER;
                props.onQuizStart?.(setup);
                return;
        }
    };

    var gameTypeOptions = [BattleshipGameTypes.SINGLE_PLAYER, BattleshipGameTypes.LIVE_MULTI_TEAM];
    if (isQuizCreatorOrEventAdmin(quiz.id!, eventAlias) && props.onCreateQuizSession) {
        gameTypeOptions.push(BattleshipGameTypes.REMOTE_COMPETITION);
    }

    return (
        <div className="aq-content">
            <h2 className="quiz-title">{quiz.name}</h2>
            <div>
                <p className="quiz-description">{quiz.description}</p>
                <p className="quiz-genre">{quiz.genreId && <>Genre: {gContext.getGenreName(quiz.genreId)}</>}</p>
            </div>
            <Divider />
            <Stack spacing={2} direction="column" alignItems="center">
                {/* add a radio button choice for the BattleshipGameTypes that will be used to switch tabs containing components for the setup */}
                <FormControl component="fieldset">
                    <RadioGroup row value={selectedGameType.toString()} onChange={handleGameTypeChange}>
                        {gameTypeOptions
                            .filter((value) => typeof value === 'number')
                            .map((gameType, index) => (
                                <FormControlLabel
                                    key={index}
                                    value={gameType.toString()}
                                    control={<Radio />}
                                    label={getGameTypeName(gameType as BattleshipGameTypes)}
                                />
                            ))}
                    </RadioGroup>
                </FormControl>
                {selectedGameType === BattleshipGameTypes.LIVE_MULTI_TEAM && (
                    <>
                        <TextField label="Team name" value={newTeamName} onChange={(event) => setNewTeamName(event.target.value)} onKeyDown={onNewTeamNameKeypress}/>
                        <Button variant="contained" onClick={addNewTeamClick} disabled={!newTeamName}>
                            Add Team
                        </Button>
                        {!!teams.length && (
                            <>
                                <h4>Teams list</h4>
                                <List sx={{ width: '100%', maxWidth: 360 }}>
                                    {teams.map((team, index) => (
                                        <ListItem disablePadding key={`list-item-${index}`}>
                                            <ListItemText
                                                key={`list-item-text-${index}`}
                                                primary={
                                                    <>
                                                        {`Team ${index + 1}:`}&nbsp;&nbsp;&nbsp;&nbsp;{team.name}
                                                    </>
                                                }
                                                secondary={
                                                    showShipLocations ? `Ship locations: ${getShipLocationNames(team.shipLocations)}` : ''
                                                }
                                            />
                                            <IconButton aria-label="delete team" onClick={() => removeTeam(team.name)}>
                                                <ClearIcon />
                                            </IconButton>
                                        </ListItem>
                                    ))}
                                </List>
                                {isQuizCreatorOrEventAdmin(quiz.id!, eventAlias) && (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                inputProps={{
                                                    'aria-label': 'Show ship locations'
                                                }}
                                                checked={showShipLocations}
                                                onChange={() => setShowShipLocations(!showShipLocations)}
                                            />
                                        }
                                        label="Show ship locations"
                                    />
                                )}
                            </>
                        )}
                        <FormGroup>
                            <FormControlLabel control={<Switch checked={manualShipSetupEnabled} onChange={onManualShipSetupChange} />} label="Manual Ship Location Setup" />
                        </FormGroup>
                        {!!manualShipSetupEnabled && (
                            <ShipManualSetup
                                columns={gridColumns}
                                rows={gridRows}
                                teamShipCount={teamShipCount}
                                teams={teams}
                                onShipLocationsChange={(newTeams: Team[]) => setTeams(newTeams)}
                            />
                        )}
                    </>
                )}

                <Stack spacing={2} direction="row" justifyContent={'center'} style={{ borderTop: '1px solid', paddingTop: '15px' }}>
                    <TextField
                        label="Number of columns"
                        type="number"
                        style={{ minWidth: '150px' }}
                        inputProps={{
                            min: 2,
                            max: maxGridDimention
                        }}
                        value={gridColumns}
                        onChange={(event) => changeGridSize(gridRows, parseInt(event.target.value))}
                    />
                    <TextField
                        label="Number of rows"
                        type="number"
                        style={{ minWidth: '150px' }}
                        inputProps={{
                            min: 2,
                            max: maxGridDimention
                        }}
                        value={gridRows}
                        onChange={(event) => changeGridSize(parseInt(event.target.value), gridColumns)}
                    />
                </Stack>
                <Typography variant="h5">Team ships</Typography>
                <Stack
                    spacing={2}
                    direction="row"
                    justifyContent={'center'}
                    style={{ borderTop: '1px solid', paddingTop: '15px', marginTop: '5px' }}
                >
                    <TextField
                        label="Submarine"
                        id="ship0"
                        type="number"
                        style={{ minWidth: '150px', maxWidth: '250px' }}
                        inputProps={{
                            min: 1,
                            max: gridRows * gridColumns
                        }}
                        value={teamShipCount[0]}
                        onChange={onTeamShipCountChange}
                    />
                    <TextField
                        label="Destroyer"
                        id="ship1"
                        type="number"
                        style={{ minWidth: '150px', maxWidth: '250px' }}
                        inputProps={{
                            min: 0,
                            max: Math.floor((gridRows * gridColumns) / 2)
                        }}
                        value={teamShipCount[1]}
                        onChange={onTeamShipCountChange}
                    />
                    <TextField
                        label="Cruiser"
                        id="ship2"
                        type="number"
                        style={{ minWidth: '150px', maxWidth: '250px' }}
                        inputProps={{
                            min: 0,
                            max: Math.floor((gridRows * gridColumns) / 3)
                        }}
                        value={teamShipCount[2]}
                        onChange={onTeamShipCountChange}
                    />
                    <TextField
                        label="Battleship"
                        id="ship3"
                        type="number"
                        style={{ minWidth: '150px', maxWidth: '250px' }}
                        inputProps={{
                            min: 0,
                            max: Math.floor((gridRows * gridColumns) / 4)
                        }}
                        value={teamShipCount[3]}
                        onChange={onTeamShipCountChange}
                    />
                    <TextField
                        label="Aircraft carrier"
                        type="number"
                        id="ship4"
                        style={{ minWidth: '150px', maxWidth: '250px' }}
                        inputProps={{
                            min: 0,
                            max: Math.floor((gridRows * gridColumns) / 5)
                        }}
                        value={teamShipCount[4]}
                        onChange={onTeamShipCountChange}
                    />
                </Stack>
                <Typography variant="caption">
                    Note: When changing grid size or number of ships per team, ship locations will be regenerated
                </Typography>
                <Typography variant="h5">Behavior</Typography>
                <Stack
                    spacing={2}
                    direction="row"
                    justifyContent={'center'}
                    style={{ borderTop: '1px solid', paddingTop: '15px', marginTop: '5px' }}
                >
                    {props.onCreateQuizSession && (
                        <>
                            <TextField
                                label="Quiz duration (seconds)"
                                type="number"
                                inputProps={{ min: 0 }}
                                value={maxDurationSec}
                                onChange={(event) => setMaxDurationSec(parseInt(event.target.value))}
                            />
                            <TextField
                                label="Question timeout (seconds)"
                                type="number"
                                inputProps={{ min: 0 }}
                                value={questionTimeSec}
                                onChange={(event) => setQuestionTimeSec(parseInt(event.target.value))}
                            />
                        </>
                    )}
                    <TextField
                        label="Quesiton cooldown (seconds)"
                        type="number"
                        inputProps={{ min: 0 }}
                        value={cooldownSeconds}
                        onChange={(event) => setCooldownSeconds(parseInt(event.target.value))}
                    />
                </Stack>

                {(quiz.publiclyAvailable || isQuizCreatorOrEventAdmin(quiz.id!, eventAlias)) && props.onQuizStart && (
                    <>
                        {selectedGameType === BattleshipGameTypes.SINGLE_PLAYER && (
                            <Button
                                variant="contained"
                                onClick={() => onSetupComplete(SetupType.SinglePlayer)}
                                disabled={teams.length !== 1}
                            >
                                Start Single Player Quiz
                            </Button>
                        )}
                        {selectedGameType === BattleshipGameTypes.LIVE_MULTI_TEAM && (
                            <Button variant="contained" onClick={() => onSetupComplete(SetupType.Live)} disabled={teams.length < 2}>
                                Start Quiz
                            </Button>
                        )}
                    </>
                )}
                {isQuizCreatorOrEventAdmin(quiz.id!, eventAlias) && (
                    <>
                        {props.onCreateQuizSession && (
                            <Button variant="contained" onClick={() => onSetupComplete(SetupType.SessionSetup)} disabled={teams.length < 1}>
                                Create Session
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            onClick={() => navigate(NavUtils.generateNavPath(NavPages.EditQuiz, quiz.id, eventContext?.alias))}
                        >
                            Edit Quiz
                        </Button>
                    </>
                )}
            </Stack>
        </div>
    );
};
